//@ts-check
import { Tooltip, Icon } from 'components/common';
import { useAuth, usePost } from 'hooks';
import React from 'react';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  ewrcaDescription,
  handleExportNotification,
  ewrcaOrganisationPurchasingDescription,
} from 'reducers/notifications';
import {
  getFilters,
  getBlockedForPurchasingExport,
  getFiltersByGroupId,
  getLastSuccessfulPurchasingQuery,
  getOrganisationResearchers,
  getOrganisationResearcherSearchError,
  getOrganisationWorkItemSearchError,
  getOrganisationResearchersLoading,
  getOrganisationView,
  getOrganisationWorkItemIsLoading,
  getOrganisationWorkItems,
  getBlockedForResearchersExport,
  getBlockedForWorkItemExport,
  getOrganisationClinicalTrialData,
  getOrganisationLoadingClinicalTrialData,
  getBlockedForClinicalTrialExport,
  getOrganisationClinicalTrialFilters,
  getOrganisationClinicalTrialSearchError,
  getPurchasingFilters,
  getPurchasingLoading,
  getPurchasingSearchRequest,
  getPurchasingSort,
  getPurchasingSortDir,
} from 'selectors/organisation';
import {
  exportOrganisationPurchasing,
  exportSearch,
  exportSubsidiaries,
  largeWarning,
} from 'services/api';
import styled from 'styled-components';
import { buildQuery } from 'utils/search';
import { buildQuery as PurchasingBuildQuery } from 'utils/purchasing';
import {
  ORGANISATIONCLINICALTRIALSVIEW,
  ORGANISATIONPEOPLEVIEW,
  ORGANISATIONPURCHASINGVIEW,
  ORGANISATIONSUBSIDIARIESVIEW,
  ORGANISATIONWORKITEMVIEW,
} from '../helpers';
import { theme } from '../../../utils/theme';
import Export from 'components/Export';
import { downloadType } from 'components/Export/Summary/constants';
import { handleFailedExportNotification } from 'components/Search/Results/Actions/notifications';
import { RemoveNonEmptyDate } from 'components/Search/Filters/keys';

const DownloadButtonContainer = styled.div`
  margin-left: auto;
`;

/***
 * @param {{organisation: {id: number, organisationName: string}}} props
 */
export default function OrganisationDownloadButton({ organisation }) {
  const [exportState, postExport] = /** @type {any} */ (usePost(
    /** @type {any} */ ({})
  ));
  const { res: exportRes, loading: loadingExport, exportError } = exportState;
  const { user } = useAuth();

  /***
   * @type {string}
   */
  const view = useSelector(getOrganisationView);
  const loadingResearchers = useSelector(
    getOrganisationResearchersLoading(organisation.id)
  );
  const loadingWorkItems = useSelector(
    getOrganisationWorkItemIsLoading(organisation.id)
  );
  const loadingClinicalTrials = useSelector(
    getOrganisationLoadingClinicalTrialData(organisation.id)
  );
  const organisationResearchers = useSelector(
    getOrganisationResearchers(organisation.id)
  );
  const organisationWorkItems = useSelector(
    getOrganisationWorkItems(organisation.id)
  );
  const organisationClinicalTrials = useSelector(
    getOrganisationClinicalTrialData(organisation.id)
  );
  const researcherFilters = useSelector(getFiltersByGroupId(organisation.id));
  const workItemFilters = useSelector(getFilters(organisation.id));
  const clinicalTrialFilters = useSelector(
    getOrganisationClinicalTrialFilters(organisation.id)
  );
  const searchError = useSelector(
    getOrganisationResearcherSearchError(organisation.id)
  );
  const workItemSearchError = useSelector(
    getOrganisationWorkItemSearchError(organisation.id)
  );
  const clinicalTrialSearchError = useSelector(
    getOrganisationClinicalTrialSearchError(organisation.id)
  );
  const researcherDownloadBlocked = useSelector(
    getBlockedForResearchersExport(organisation.id)
  );
  const workItemDownloadBlocked = useSelector(
    getBlockedForWorkItemExport(organisation.id)
  );
  const clinicalTrialDownloadBlocked = useSelector(
    getBlockedForClinicalTrialExport(organisation.id)
  );
  const purchasingSearchRequest = useSelector(
    getPurchasingSearchRequest(organisation.id)
  );
  const purchasingDownloadBlocked = useSelector(getBlockedForPurchasingExport);
  const purchasingFilters = useSelector(getPurchasingFilters);
  const purchasingLoading = useSelector(getPurchasingLoading);
  const purchasingSort = useSelector(getPurchasingSort);
  const purchasingSortDir = useSelector(getPurchasingSortDir);
  const lastSuccessfulPurchasingQuery = useSelector(
    getLastSuccessfulPurchasingQuery
  );

  const getPrefixFilter = view => {
    if (view === ORGANISATIONCLINICALTRIALSVIEW)
      return `Category:ClinicalTrials`;

    return `SuperOrganisationId:${organisation.id}`;
  };

  const getExportBody = () => {
    const [query, highlightQuery, terms, nestedQueries] = buildQuery(
      filters(),
      null,
      getPrefixFilter(view),
      true
    );
    let searchedFilters = {};
    switch (view) {
      case ORGANISATIONCLINICALTRIALSVIEW:
        searchedFilters = { ...clinicalTrialFilters };
        break;
      case ORGANISATIONPEOPLEVIEW:
        searchedFilters = { ...researcherFilters };
        break;
      default:
        searchedFilters = { ...workItemFilters };
    }
    RemoveNonEmptyDate(searchedFilters);
    const structuredSearch = JSON.stringify(searchedFilters);
    return {
      query: query,
      highlightQuery: highlightQuery,
      nestedQueries: nestedQueries.length > 0 ? nestedQueries : null,
      orderBy: 'desc',
      filters: { ...terms },
      orgPageExportId: organisation.id,
      isOrganisationSponsorExport: view === ORGANISATIONCLINICALTRIALSVIEW,
      structuredSearch: structuredSearch,
    };
  };

  const exportHandler = params => {
    if (view === ORGANISATIONSUBSIDIARIESVIEW) {
      // @ts-ignore
      postExport({
        url: exportSubsidiaries(organisation.id),
        body: { ...params },
      });
      return;
    }

    if (view === ORGANISATIONPURCHASINGVIEW) {
      const { supplierName, ...tmpFilters } = purchasingFilters;
      RemoveNonEmptyDate(tmpFilters);
      const query = PurchasingBuildQuery(tmpFilters);
      // @ts-ignore
      postExport({
        url: purchasingSearchRequest?.tooLargeToDownload
          ? largeWarning('Purchasing')
          : exportOrganisationPurchasing,
        body: {
          ...params,
          query,
          structuredSearch: JSON.stringify(tmpFilters),
          order: purchasingSort,
          orderBy: purchasingSortDir,
          orgPageExportId: organisation.id,
        },
      });
      return;
    }

    const body = getExportBody();
    // @ts-ignore
    postExport({
      url: ewrca() ? largeWarning('UniversalSearch') : exportSearch,
      body: { ...body, ...params },
    });
  };

  const Help = () => (
    <Tooltip
      title={
        view === ORGANISATIONCLINICALTRIALSVIEW
          ? 'Export provides all contacts and organisations associated with clinical trials on this page'
          : view === ORGANISATIONPURCHASINGVIEW
          ? 'Export includes all products sold by this organisation'
          : 'Export includes all subsidiary organisations on this page'
      }
      placement='right'
    >
      <div style={{ cursor: 'pointer', paddingRight: '5px' }}>
        {/* @ts-ignore */}
        <Icon icon='help' color={theme.teal} size='xs' />
      </div>
    </Tooltip>
  );

  const filters = () => {
    if (view === ORGANISATIONPEOPLEVIEW) return researcherFilters;
    if (view === ORGANISATIONWORKITEMVIEW) return workItemFilters;
    if (view === ORGANISATIONCLINICALTRIALSVIEW) return clinicalTrialFilters;
    if (view === ORGANISATIONPURCHASINGVIEW) return purchasingFilters;
  };

  const showDownloadButton =
    (view === ORGANISATIONPEOPLEVIEW ||
      view === ORGANISATIONWORKITEMVIEW ||
      view === ORGANISATIONCLINICALTRIALSVIEW ||
      view === ORGANISATIONPURCHASINGVIEW ||
      view === ORGANISATIONSUBSIDIARIESVIEW) &&
    !user.isRateLimited &&
    !user.disableExport;

  const ewrca = () => {
    if (view === ORGANISATIONPEOPLEVIEW) return organisationResearchers?.ewrca;
    if (view === ORGANISATIONWORKITEMVIEW) return organisationWorkItems?.ewrca;
    if (view === ORGANISATIONCLINICALTRIALSVIEW)
      return organisationClinicalTrials?.ewrca;
    if (view === ORGANISATIONPURCHASINGVIEW)
      return purchasingSearchRequest?.tooLargeToDownload;
  };

  const currentPanelSearchError = () => {
    if (view === ORGANISATIONPEOPLEVIEW) return searchError;
    if (view === ORGANISATIONWORKITEMVIEW) return workItemSearchError;
    if (view === ORGANISATIONCLINICALTRIALSVIEW)
      return clinicalTrialSearchError;
  };

  const currentPanelExportBlocked = () => {
    if (view === ORGANISATIONPEOPLEVIEW) return researcherDownloadBlocked;
    if (view === ORGANISATIONWORKITEMVIEW) return workItemDownloadBlocked;
    if (view === ORGANISATIONCLINICALTRIALSVIEW)
      return clinicalTrialDownloadBlocked;
    if (view === ORGANISATIONPURCHASINGVIEW) return purchasingDownloadBlocked;
  };

  useEffect(() => {
    if (exportRes) {
      handleExportNotification();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportRes]);

  useEffect(() => {
    if (exportError) {
      handleFailedExportNotification(exportError.status);
    }
  }, [exportError]);

  if (
    !(
      view === ORGANISATIONPEOPLEVIEW ||
      view === ORGANISATIONWORKITEMVIEW ||
      view === ORGANISATIONCLINICALTRIALSVIEW ||
      view === ORGANISATIONPURCHASINGVIEW ||
      view === ORGANISATIONSUBSIDIARIESVIEW
    )
  ) {
    return null;
  }

  const exportButton = (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {(view === ORGANISATIONCLINICALTRIALSVIEW ||
        view === ORGANISATIONPURCHASINGVIEW ||
        view === ORGANISATIONSUBSIDIARIESVIEW) && <Help />}
      <Export
        disabled={
          loadingExport ||
          (view !== ORGANISATIONSUBSIDIARIESVIEW &&
            view !== ORGANISATIONPURCHASINGVIEW &&
            (loadingResearchers ||
              loadingWorkItems ||
              loadingClinicalTrials)) ||
          currentPanelSearchError() ||
          (view === ORGANISATIONPEOPLEVIEW &&
            (organisationResearchers?.count === 0 ||
              researcherDownloadBlocked)) ||
          (view === ORGANISATIONWORKITEMVIEW &&
            (organisationWorkItems?.totalCount === 0 ||
              workItemDownloadBlocked)) ||
          (view === ORGANISATIONCLINICALTRIALSVIEW &&
            (organisationClinicalTrials?.totalCount === 0 ||
              clinicalTrialDownloadBlocked)) ||
          (view === ORGANISATIONPURCHASINGVIEW &&
            (purchasingLoading ||
              purchasingDownloadBlocked ||
              !lastSuccessfulPurchasingQuery ||
              purchasingSearchRequest?.totalResults === 0)) ||
          ewrca()
        }
        ewrca={!currentPanelExportBlocked() && ewrca()}
        downloadType={
          view === ORGANISATIONPEOPLEVIEW
            ? downloadType.organisationResearchersSearch
            : view === ORGANISATIONWORKITEMVIEW
            ? downloadType.organisationActivitySearch
            : view === ORGANISATIONCLINICALTRIALSVIEW
            ? downloadType.organisationClinicalTrialsSearch
            : view === ORGANISATIONPURCHASINGVIEW
            ? downloadType.organisationPurchasingSearch
            : downloadType.subsidiaries
        }
        canUseCredits={
          view !== ORGANISATIONSUBSIDIARIESVIEW &&
          view !== ORGANISATIONPURCHASINGVIEW
        }
        getQueryBody={
          view === ORGANISATIONPURCHASINGVIEW ? undefined : getExportBody
        }
        onExport={exportHandler}
        ewrcaTooltip={
          view === ORGANISATIONPURCHASINGVIEW
            ? ewrcaOrganisationPurchasingDescription
            : ewrcaDescription
        }
      >
        Export Data
      </Export>
    </div>
  );

  return (
    <DownloadButtonContainer>
      {showDownloadButton && currentPanelExportBlocked() && (
        <Tooltip
          placement='topRight'
          title='You need to update the search to reactivate export'
        >
          {exportButton}
        </Tooltip>
      )}
      {showDownloadButton && !currentPanelExportBlocked() && exportButton}
    </DownloadButtonContainer>
  );
}
